import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { PageHeader, SectionCard, StatCard, EmptyState } from "../components/ui/kaizen";
import { Money, EnvelopeSimple, ChatCircleText, PaperPlaneTilt, CheckCircle, XCircle, Warning, ArrowsClockwise } from "@phosphor-icons/react";

const StatusPill = ({ status, label }) => {
  const map = {
    sent: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
    error: "bg-red-500/15 text-red-300 border-red-500/30",
    skipped: "bg-zinc-700/50 text-zinc-400 border-zinc-700",
  };
  const Icon = status === "sent" ? CheckCircle : status === "error" ? XCircle : null;
  return (
    <span className={`inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full border ${map[status] || map.skipped}`}>
      {Icon && <Icon size={12} weight="fill" />} {label}
    </span>
  );
};

export default function RelancesImpayesModule() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sendingId, setSendingId] = useState(null);
  const [testOpen, setTestOpen] = useState(false);
  const [testEmail, setTestEmail] = useState("");
  const [testPhone, setTestPhone] = useState("");
  const [testing, setTesting] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/relances-impayes");
      setData(data);
    } catch (e) {
      toast.error("Chargement impossible", { description: e?.response?.data?.detail || "" });
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const toggleAuto = async () => {
    const next = !data.auto_enabled;
    try {
      await api.patch("/relances-impayes/config", { auto_enabled: next });
      setData((d) => ({ ...d, auto_enabled: next }));
      toast.success(next ? "Relance automatique ACTIVÉE" : "Relance automatique désactivée");
    } catch (e) { toast.error("Échec de la mise à jour du réglage."); }
  };

  const relancer = async (contract_id, name) => {
    setSendingId(contract_id);
    try {
      const { data: res } = await api.post("/relances-impayes/send", { contract_id });
      const ok = res.email_status === "sent" || res.sms_status === "sent";
      if (ok) toast.success(`Relance envoyée à ${name}`, { description: `Courriel: ${res.email_status} · SMS: ${res.sms_status}` });
      else toast.error(`Rien n'a pu être envoyé à ${name}`, { description: `${res.email_error || ""} ${res.sms_error || ""}`.trim() });
      await load();
    } catch (e) {
      toast.error("Envoi impossible", { description: e?.response?.data?.detail || "" });
    } finally { setSendingId(null); }
  };

  const runTest = async () => {
    if (!testEmail && !testPhone) { toast.error("Renseigne un courriel et/ou un numéro."); return; }
    setTesting(true);
    try {
      const { data: res } = await api.post("/relances-impayes/test", { email: testEmail, phone: testPhone });
      toast.success("Test envoyé", { description: `Courriel: ${res.email_status} · SMS: ${res.sms_status}` });
    } catch (e) { toast.error("Test impossible", { description: e?.response?.data?.detail || "" }); }
    finally { setTesting(false); }
  };

  if (loading && !data) return <div className="p-8 text-zinc-400" data-testid="relances-loading">Chargement…</div>;
  const c = data?.counts || {};

  return (
    <div className="pb-16" data-testid="relances-impayes-page">
      <PageHeader
        title="💸 Relances impayés"
        subtitle="Envoi automatique du lien de paiement (courriel + SMS) aux membres dont le paiement a échoué."
        icon={Money}
        actions={
          <div className="flex items-center gap-3 flex-wrap">
            <button
              onClick={toggleAuto}
              data-testid="toggle-auto-relance"
              className={`px-4 py-2 rounded-lg text-sm font-semibold border transition-colors ${
                data?.auto_enabled
                  ? "bg-emerald-500/15 text-emerald-300 border-emerald-500/30 hover:bg-emerald-500/25"
                  : "bg-zinc-800 text-zinc-400 border-zinc-700 hover:bg-zinc-700"
              }`}
            >
              {data?.auto_enabled ? "🟢 Auto : ACTIVÉE" : "⚪ Auto : désactivée"}
            </button>
            <button onClick={() => setTestOpen((o) => !o)} data-testid="open-test-relance"
              className="px-4 py-2 rounded-lg text-sm font-semibold bg-zinc-800 text-zinc-200 border border-zinc-700 hover:bg-zinc-700 transition-colors">
              Tester un envoi
            </button>
            <button onClick={load} data-testid="refresh-relances"
              className="px-3 py-2 rounded-lg text-sm bg-zinc-800 text-zinc-300 border border-zinc-700 hover:bg-zinc-700 flex items-center gap-2">
              <ArrowsClockwise size={15} /> Rafraîchir
            </button>
          </div>
        }
      />

      <div className="px-8 lg:px-12 py-6 space-y-6">
        {/* Config / statut des canaux */}
        <div className="flex flex-wrap items-center gap-3 text-xs" data-testid="relance-config-banner">
          {[
            ["Courriel (Resend)", data?.resend_configured],
            ["SMS (Twilio)", data?.twilio_configured],
            ["Webhook Stripe signé", data?.webhook_configured],
          ].map(([label, ok]) => (
            <span key={label} className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border ${
              ok ? "bg-emerald-500/10 text-emerald-300 border-emerald-500/30" : "bg-amber-500/10 text-amber-300 border-amber-500/30"}`}>
              {ok ? <CheckCircle size={14} weight="fill" /> : <Warning size={14} weight="fill" />} {label}
            </span>
          ))}
        </div>

        {/* Panneau de test */}
        {testOpen && (
          <SectionCard title="Tester un envoi (aperçu)" icon={PaperPlaneTilt}>
            <p className="text-sm text-zinc-400">Envoie un exemple de relance à ton propre courriel / numéro pour valider le rendu (montant fictif de 150 $, lien démo).</p>
            <div className="flex flex-wrap gap-3">
              <input data-testid="test-email-input" value={testEmail} onChange={(e) => setTestEmail(e.target.value)} placeholder="ton@courriel.com"
                className="flex-1 min-w-[220px] bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-sm text-zinc-100 placeholder:text-zinc-500" />
              <input data-testid="test-phone-input" value={testPhone} onChange={(e) => setTestPhone(e.target.value)} placeholder="819-527-9446"
                className="flex-1 min-w-[220px] bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-sm text-zinc-100 placeholder:text-zinc-500" />
              <button onClick={runTest} disabled={testing} data-testid="run-test-relance"
                className="px-5 py-2 rounded-lg text-sm font-semibold bg-[#D90429] text-white hover:bg-[#b8031f] disabled:opacity-50 flex items-center gap-2">
                <PaperPlaneTilt size={15} /> {testing ? "Envoi…" : "Envoyer le test"}
              </button>
            </div>
          </SectionCard>
        )}

        {/* KPIs */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <StatCard label="Contrats en retard" value={c.overdue ?? 0} icon={Warning} />
          <StatCard label="Relances envoyées" value={c.sent ?? 0} icon={CheckCircle} />
          <StatCard label="Total relances journalisées" value={c.followups ?? 0} icon={Money} />
        </div>

        {/* Contrats en retard */}
        <SectionCard title="Contrats en retard — à relancer" icon={Warning}>
          {(!data?.overdue || data.overdue.length === 0) ? (
            <EmptyState icon={CheckCircle} title="Aucun impayé 🎉" description="Aucun contrat en retard actuellement." />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="overdue-table">
                <thead>
                  <tr className="text-left text-zinc-400 text-xs uppercase border-b border-zinc-800">
                    <th className="py-2 pr-3">Élève</th>
                    <th className="py-2 pr-3">Contact</th>
                    <th className="py-2 pr-3">Tarif</th>
                    <th className="py-2 pr-3">Échéance</th>
                    <th className="py-2 pr-3">Dernière relance</th>
                    <th className="py-2 pr-3 text-right">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {data.overdue.map((o) => (
                    <tr key={o.id} className="border-b border-zinc-800/60" data-testid={`overdue-row-${o.id}`}>
                      <td className="py-2.5 pr-3 font-medium text-zinc-100">{o.full_name || "—"}</td>
                      <td className="py-2.5 pr-3 text-zinc-400">
                        <div className="flex flex-col">
                          <span className="truncate max-w-[220px]">{o.email || o.parent_email || "—"}</span>
                          <span className="text-zinc-500">{o.phone || "—"}</span>
                        </div>
                      </td>
                      <td className="py-2.5 pr-3 text-zinc-300">{o.tarif_mensuel ? `${Number(o.tarif_mensuel).toFixed(2)} $` : "—"}</td>
                      <td className="py-2.5 pr-3 text-zinc-400">{o.prochain_paiement_du || "—"}</td>
                      <td className="py-2.5 pr-3 text-zinc-500">{o.last_payment_followup_at ? o.last_payment_followup_at.slice(0, 10) : "jamais"}</td>
                      <td className="py-2.5 pr-3 text-right">
                        <button
                          onClick={() => relancer(o.id, o.full_name)}
                          disabled={sendingId === o.id}
                          data-testid={`relance-btn-${o.id}`}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-[#D90429] text-white hover:bg-[#b8031f] disabled:opacity-50">
                          <PaperPlaneTilt size={13} /> {sendingId === o.id ? "Envoi…" : "Relancer"}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </SectionCard>

        {/* Historique */}
        <SectionCard title="Historique des relances" icon={EnvelopeSimple}>
          {(!data?.followups || data.followups.length === 0) ? (
            <EmptyState icon={EnvelopeSimple} title="Aucune relance encore" description="Les relances (auto ou manuelles) apparaîtront ici avec leur statut par canal." />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="followups-table">
                <thead>
                  <tr className="text-left text-zinc-400 text-xs uppercase border-b border-zinc-800">
                    <th className="py-2 pr-3">Date</th>
                    <th className="py-2 pr-3">Élève</th>
                    <th className="py-2 pr-3">Montant</th>
                    <th className="py-2 pr-3"><EnvelopeSimple size={13} className="inline" /> Courriel</th>
                    <th className="py-2 pr-3"><ChatCircleText size={13} className="inline" /> SMS</th>
                    <th className="py-2 pr-3">Source</th>
                  </tr>
                </thead>
                <tbody>
                  {data.followups.map((f) => (
                    <tr key={f.id} className="border-b border-zinc-800/60" data-testid={`followup-row-${f.id}`}>
                      <td className="py-2.5 pr-3 text-zinc-400">{(f.created_at || "").slice(0, 16).replace("T", " ")}</td>
                      <td className="py-2.5 pr-3 text-zinc-100 font-medium">{f.full_name || "—"}</td>
                      <td className="py-2.5 pr-3 text-zinc-300">{f.amount ? `${Number(f.amount).toFixed(2)} $` : "—"}</td>
                      <td className="py-2.5 pr-3"><StatusPill status={f.email_status} label={f.email_status} /></td>
                      <td className="py-2.5 pr-3"><StatusPill status={f.sms_status} label={f.sms_status} /></td>
                      <td className="py-2.5 pr-3 text-zinc-500">{f.source === "auto_webhook" ? "Auto" : "Manuel"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </SectionCard>
      </div>
    </div>
  );
}
